import { observable, action, runInAction, computed, toJS } from 'mobx';
import { useSnackBars } from '../alerts/SnackBarProvider';
import authStore from './authStore';
import { safeEval } from '../components/util/UtilFunctions';

class OrgStore {
  @observable state = 'loading';
  @observable orgUsers;
  @observable settings = null;
  @observable permissions = null;
  @observable isOwner = null;
  @observable joyrides = null;
  @observable topAgents = null;
  @observable userContactId = null;
  @observable userId = null;
  @observable setupTasks = null;
  @observable userNotified = false;
  @observable dashboardConfig = [
    [
      { Name: 'totalDealsGross' },
      { Name: 'completeDealsGross' },
      { Name: 'unmappedDealsGross' },
      { Name: 'atRiskDealsGross' },
    ],
    [{ Name: 'syncStatus' }, { Name: 'topAgents' }],
    [{ Name: 'unpaidVouchers' }],
    [{ Name: 'grossIncome' }],
  ];
  // @observable dashboardConfig = [
  //   {
  //     Name: 'Unpaid Vouchers',
  //     Display: true,
  //   },
  //   {
  //     Name: 'Gross Income',
  //     Display: true,
  //   },
  //   {
  //     Name: 'Sync Status',
  //     Display: true,
  //   },
  //   {
  //     Name: 'Top Agents',
  //     Display: true,
  //   },
  //   {
  //     Name: 'Reports',
  //     Display: true,
  //     VisibleReports: [],
  //   },
  // ];

  @action
  reset() {
    this.state = 'loading';
    this.orgUsers = null;
    this.settings = null;
    this.permissions = null;
    this.isOwner = null;
    this.joyrides = null;
    this.topAgents = null;
    this.setupTasks = null;
    this.userNotified = false;
    this.dashboardConfig = [
      {
        Name: 'Unpaid Vouchers',
        Display: true,
      },
      {
        Name: 'Gross Income',
        Display: true,
      },
      {
        Name: 'Sync Status',
        Display: true,
      },
      {
        Name: 'Top Agents',
        Display: true,
      },
      {
        Name: 'Reports',
        Display: true,
        VisibleReports: [],
      },
    ];
  }

  @computed
  get setupStatus() {
    if (this.setupTasks) {
      const itemsList = toJS(this.setupTasks)
        .map(task => task.Items)
        .flat();

      return itemsList.every(item => item.IsOk);
    }
  }

  @computed
  get orgUserArray() {
    return this.orgUsers;
  }

  @computed
  get integrationStatus() {
    return safeEval(() => this.settings.ExtSysPush, 0);
  }

  @computed
  get isIntegrated() {
    return safeEval(() => this.settings.ExtSysId > 0, false);
  }

  isNewJoyride = id => {
    if (!this.joyrides) return false;
    return this.joyrides.findIndex(i => i.Joyride === id) === -1;
  };

  @action
  async retrieveConfig() {
    try {
      this.state = 'loading';
      // const tasks = await this.retrieveChecklist();
      const result = await this.fetchConfig();
      const { Settings, Permissions, User, DashboardConfig } = result;
      const { ContactId, IsOwner, UserId } = User;
      // after await, modifying state again, needs an action:
      runInAction(() => {
        // this.setupTasks = tasks;
        this.settings = Settings;
        this.permissions = Permissions;
        this.isOwner = IsOwner;
        this.userContactId = ContactId;
        this.userId = UserId;
        this.state = 'done';
        if (DashboardConfig) this.dashboardConfig = DashboardConfig;
      });
    } catch (error) {
      console.error(error);
      runInAction(() => {
        this.state = 'error';
      });
    }
  }

  @action
  async retrieveChecklist() {
    try {
      const tasks = await this.fetchChecklist();
      const { JSON_VAL } = tasks;
      this.setupTasks = JSON_VAL;
    } catch (error) {
      console.error(error);
      runInAction(() => {
        this.state = 'error';
      });
    }
  }

  @action
  async retrieveOrgUsers() {
    try {
      const data = await this.fetchOrgUsers();
      runInAction(() => {
        this.orgUsers = data;
      });
    } catch (error) {
      console.error(error);
    }
  }

  @action
  async retrieveJoyrides() {
    try {
      const data = await this.fetchJoyrides();
      runInAction(() => {
        this.joyrides = data;
      });
    } catch (error) {
      console.error(error);
    }
  }

  @action
  async retrieveTopAgents() {
    try {
      const data = await this.fetchTopAgents();
      runInAction(() => {
        this.topAgents = data;
      });
    } catch (error) {
      console.error(error);
    }
  }

  @action
  notifyUser() {
    this.userNotified = true;
  }

  @action
  updateDashboardConfig(val) {
    this.dashboardConfig = val;
  }

  fetchConfig = async () => {
    try {
      const { data } = await authStore.request.get(
        `${process.env.REACT_APP_API}/api/config`,
      );
      return data;
    } catch (e) {
      console.error(e);
    }
  };

  fetchOrgUsers = async () => {
    const { data } = await authStore.request.get(
      `${process.env.REACT_APP_API}/api/organization/users`,
    );
    return data;
  };

  fetchJoyrides = async () => {
    const { data } = await authStore.request.get(
      `${process.env.REACT_APP_API}/api/joyrides`,
    );
    return data;
  };

  fetchTopAgents = async () => {
    const { data } = await authStore.request.get(
      `${process.env.REACT_APP_API}/api/dashboard-agent`,
    );
    return data;
  };

  fetchChecklist = async () => {
    try {
      const res = await authStore.request.get(
        `${process.env.REACT_APP_API}/api/setup-checklist`,
      );
      const { data } = res;

      return data;
    } catch (error) {
      console.log(error);
    }
  };
}

const orgStore = new OrgStore();

export default orgStore;
